/**
 * CritiqueSessionCreationDialog
 *
 * Dialog for creating a human critique session with feedback options.
 * Feature: 649-critique-feedback-options
 * Extended by: 651-n-comments-task
 */

import { useState, useEffect } from 'react';
import { Modal } from '@/components/composed/Modal';
import { Button } from '@/components/atoms';
import { FeedbackOptionsSelector } from '@/components/composed/FeedbackOptionsSelector';
import type { FeedbackType, NCommentsConfig } from '@/types/photo-rating';
import logger from '@/utils/logger';

export interface CritiqueSessionCreationDialogProps {
  open: boolean;
  onClose: () => void;
  /** Feature 651: Extended to include nCommentsConfig when n_comments is enabled */
  onConfirm: (sessionTitle: string, feedbackOptions: FeedbackType[], nCommentsConfig?: NCommentsConfig) => void;
  collectionName: string;
  /** Number of photos in the collection (required for n_comments option) */
  photoCount?: number;
  isCreating?: boolean;
}

export function CritiqueSessionCreationDialog({
  open,
  onClose,
  onConfirm,
  collectionName,
  photoCount,
  isCreating = false,
}: CritiqueSessionCreationDialogProps) {
  const [sessionTitle, setSessionTitle] = useState('');
  const [feedbackOptions, setFeedbackOptions] = useState<FeedbackType[]>(['general']);
  const [nCommentsConfig, setNCommentsConfig] = useState<NCommentsConfig | undefined>(undefined);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSessionTitle(`Discussion for ${collectionName}`);
      setFeedbackOptions(['general']);
      setNCommentsConfig(undefined);
    }
  }, [open, collectionName]);

  const handleConfirm = () => {
    if (!sessionTitle.trim() || feedbackOptions.length === 0) {
      return;
    }
    // Include nCommentsConfig only if n_comments is enabled
    const config = feedbackOptions.includes('n_comments') ? nCommentsConfig : undefined;
    logger.info('UI', 'Critique session creation confirmed', {
      title: sessionTitle,
      feedbackOptions,
      nCommentsConfig: config,
    });
    onConfirm(sessionTitle.trim(), feedbackOptions, config);
  };

  const isValid = sessionTitle.trim() && feedbackOptions.length > 0;

  const footer = (
    <div className="flex justify-end gap-2">
      <Button variant="secondary" onClick={onClose} disabled={isCreating}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleConfirm}
        disabled={!isValid || isCreating}
      >
        {isCreating ? 'Creating...' : 'Start Discussion'}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Start Discussion"
      size="md"
      footer={footer}
    >
      <div className="space-y-6">
        {/* Session Title */}
        <div>
          <label htmlFor="session-title" className="block text-sm font-medium text-foreground mb-1">
            Session Title
          </label>
          <input
            id="session-title"
            type="text"
            value={sessionTitle}
            onChange={(e) => setSessionTitle(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter session title..."
            disabled={isCreating}
          />
        </div>

        {/* Feedback Options (Feature 651: Extended with N Comments) */}
        <FeedbackOptionsSelector
          value={feedbackOptions}
          onChange={setFeedbackOptions}
          nCommentsConfig={nCommentsConfig}
          onNCommentsConfigChange={setNCommentsConfig}
          photoCount={photoCount}
          disabled={isCreating}
          error={feedbackOptions.length === 0 ? 'At least one feedback option is required' : undefined}
        />
      </div>
    </Modal>
  );
}
